/**
 * Phase 7 device-facing API client (docs/android/PHASE-7-ARCHITECTURE.md
 * SS3-6): pairing-code redemption and heartbeat reporting, called only from
 * DevicePairingModal.tsx and lib/deviceHeartbeat.ts on the Android kiosk.
 *
 * Deliberately NOT built on lib/api.ts's `api` object: these calls carry no
 * user session at all - the credential is `X-Device-Id` + `X-Device-Secret`
 * (apps/api/src/http/middleware/deviceAuth.ts's own file doc), never a
 * session cookie or bearer token - and a bad/stale device secret must never
 * trigger that client's sign-out-on-401 flow, which exists for a signed-in
 * PERSON's session, a completely different concern from this device's own
 * identity. `apiUrl()` is reused from there so both clients resolve the
 * same real API origin (VITE_API_BASE_URL) rather than keeping two copies
 * of that logic to drift apart.
 */
import type { SyncPushRequest, SyncPushResponse } from '@kortex/shared';
import { apiUrl } from './api';
import { getDeviceSecret } from './deviceCredential';

export class DeviceApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'DeviceApiError';
    this.status = status;
    this.code = code;
  }
}

async function post<T>(
  path: string,
  body: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  return send<T>('POST', path, body, headers);
}

async function send<T>(
  method: 'GET' | 'POST',
  path: string,
  body: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: method === 'POST' ? { 'Content-Type': 'application/json', ...headers } : headers,
    ...(method === 'POST' ? { body: JSON.stringify(body) } : {}),
  });
  const payload = (await res.json().catch(() => null)) as {
    data?: T;
    error?: { code: string; message: string };
  } | null;
  if (!res.ok) {
    throw new DeviceApiError(
      res.status,
      payload?.error?.code ?? 'UNKNOWN',
      payload?.error?.message ?? `Request failed (${res.status})`,
    );
  }
  return (payload?.data ?? (payload as T)) as T;
}

export interface RedeemPairingCodeInput {
  code: string;
  deviceId: string;
  deviceCode: string;
  androidVersion?: string;
  appVersion?: string;
}

/**
 * Exchanges a still-valid, admin-generated pairing code for this device's
 * own credential. No `X-Device-*` headers on this one call - there is no
 * credential to send yet, that is exactly what it hands back
 * (apps/api/src/modules/devices/device.routes.ts's own comment). The caller
 * (DevicePairingModal.tsx) is responsible for persisting the returned
 * secret via `setDeviceSecret()` - this function only moves bytes over the
 * wire, same division of concerns as deviceCredential.ts staying storage-only.
 */
export async function redeemDevicePairingCode(
  input: RedeemPairingCodeInput,
): Promise<{ deviceSecret: string }> {
  return post('/api/device/pairing/redeem', input);
}

export interface DeviceHeartbeatInput {
  appVersion?: string;
  androidVersion?: string;
  dbSchemaVersion?: number;
  kioskActive?: boolean;
  networkOnline?: boolean;
  pendingSyncCount?: number;
  failedSyncCount?: number;
  lastSyncError?: string | null;
}

/**
 * Reports this device's own telemetry - what `computeDeviceHealth()`
 * (apps/api/src/modules/devices/service.ts) reads to stop showing OFFLINE.
 * Throws `DeviceApiError` with code `NOT_PAIRED` (no network call made) when
 * this device holds no credential yet - lib/deviceHeartbeat.ts treats that
 * as "nothing to do yet", not a failure worth logging.
 */
export async function sendDeviceHeartbeat(deviceId: string, input: DeviceHeartbeatInput): Promise<void> {
  const secret = getDeviceSecret();
  if (!secret) throw new DeviceApiError(0, 'NOT_PAIRED', 'This device has not been paired yet');
  await post<void>('/api/device/heartbeat', input, {
    'X-Device-Id': deviceId,
    'X-Device-Secret': secret,
  });
}

/** This device's credential, captured once - a wipe clears it mid-command. */
export interface DeviceCredentials {
  deviceId: string;
  secret: string;
}

export function deviceCredentials(deviceId: string): DeviceCredentials | null {
  const secret = getDeviceSecret();
  return secret ? { deviceId, secret } : null;
}

const credentialHeaders = (credentials: DeviceCredentials) => ({
  'X-Device-Id': credentials.deviceId,
  'X-Device-Secret': credentials.secret,
});

export interface PendingDeviceCommand {
  id: string;
  type: string;
  payload: unknown;
  expiresAt: string;
}

/** Commands an admin has issued to this device (Device Management). */
export async function fetchDeviceCommands(credentials: DeviceCredentials): Promise<PendingDeviceCommand[]> {
  return send<PendingDeviceCommand[]>('GET', '/api/device/commands', undefined, credentialHeaders(credentials));
}

/** This device's unsent bills, before it erases itself (lib/finalSync.ts). */
export async function sendFinalSync(credentials: DeviceCredentials, request: SyncPushRequest): Promise<SyncPushResponse> {
  return post<SyncPushResponse>('/api/device/final-sync', request, credentialHeaders(credentials));
}

export async function acknowledgeDeviceCommand(
  credentials: DeviceCredentials,
  commandId: string,
  outcome: { status: 'RECEIVED' | 'PROCESSING' | 'COMPLETED' | 'FAILED'; result?: Record<string, unknown>; error?: string },
): Promise<void> {
  await post<void>(`/api/device/commands/${commandId}/ack`, outcome, credentialHeaders(credentials));
}
